/* ----------------------------------------------------------------------------
 * Copilot data source — the ONE mock/real gate for the AI surface, mirroring
 * src/data/sources.ts. The real endpoint is the AuraGen AI service's
 * POST /v1/ai/assist (session cookie + CSRF ride through the api client).
 *
 * Contract (product-neutral; workspace comes from the SESSION, never sent):
 *   request : { prompt, engine?, subject_type?, subject_id?, context?, project_id? }
 *   response: EITHER an advisory proposal
 *             { advisory:true, proposal:{text,rationale,confidence}, provenance,
 *               display_text?, narration_segments?, domain_scope? }
 *             OR a domain-choice clarification (multi-domain awareness)
 *             { advisory:true, needs_domain_choice:true, reply, domains[],
 *               display_text?, narration_segments?, provenance }
 *
 * Multi-domain (Phase 1): when a workspace has more than one connected domain
 * and none is active, an SEO/keyword question returns `needs_domain_choice` with
 * the domains to pick; selecting one (setActiveDomain) then re-issues the
 * question so Aura answers scoped to that domain. `active-domain` is persisted
 * SERVER-SIDE per user/workspace (validated to the workspace); localStorage is
 * only an instant-restore mirror, never trusted for tenancy.
 *
 * provenance is PLATFORM-BRANDED (always { provider:'Aura', model:'Copilot' }).
 * -------------------------------------------------------------------------- */

import { api, ApiError, USE_MOCK_DATA } from '@/lib/api';
import {
  DRAFT_INTAKE_PATHS,
  buildIntakeBody,
  classifyIntakeResponse,
  type DraftHandoff,
  type IntakeResponseShape,
} from '@/voice/actions/contentActionModel';
import type { Confidence } from '@/types';
import type { BriefingSection } from '@/voice/briefing/contract';

export interface AssistContext {
  /** active engine key from the route (e.g. 'sales'), if any. */
  engine?: string | null;
  subject_type?: string | null;
  subject_id?: number | null;
  /** small flat record summary (server caps at 4KB, sanitizes strings). */
  context?: Record<string, string | number | boolean | null>;
  /** explicit SEO domain to scope THIS request to (e.g. right after picking a
   *  chip). Validated + persisted server-side; never trusted for tenancy. */
  projectId?: number | null;
}

/** One spoken segment with the silence (ms) to hold AFTER it (client-timed). */
export interface AssistNarrationSegment {
  text: string;
  /** silence (ms) to hold BEFORE this segment (client-timed). Optional/back-
   *  compat (defaults 0); brackets a spoken section LABEL (owner 2026-07-19). */
  pauseBeforeMs?: number;
  pauseAfterMs: number;
}

/** A selectable connected domain (SEO project) — safe client identity only. */
export interface DomainOption {
  project_id: number;
  hostname: string;
  display_name: string;
}

/** The domain an answer was scoped to (echoed back for a "Viewing:" chip). */
export interface DomainScope {
  hostname: string;
  display_name: string;
  project_id: number;
}

export interface AssistProposalResponse {
  advisory: true;
  needs_domain_choice?: false;
  proposal: {
    text: string;
    rationale: string;
    confidence: Confidence;
  };
  provenance: { provider: string; model: string };
  /** clean, markdown-free text for display + TTS (falls back to proposal.text). */
  display_text?: string;
  /** paced spoken segments (falls back to display_text/proposal.text). */
  narration_segments?: AssistNarrationSegment[];
  /** the domain this reply was scoped to, or null for a workspace-wide answer. */
  domain_scope?: DomainScope | null;
  /** ADDITIVE (2026-07-19): organised DISPLAY blocks (short heading + short
   *  paragraphs) so a conversational answer renders like the briefing instead of
   *  one dense wall. Optional/back-compat; reuses the briefing's section shape. */
  sections?: BriefingSection[];
}

export interface AssistDomainChoiceResponse {
  advisory: true;
  needs_domain_choice: true;
  /** the spoken/shown clarification question. */
  reply: string;
  /** the domains to choose from (rendered as chips). */
  domains: DomainOption[];
  display_text?: string;
  narration_segments?: AssistNarrationSegment[];
  provenance: { provider: string; model: string };
}

/** The BE turn-close BACKSTOP shape (AiRoutes.php isTurnClose(), owner #2.2 P1
 *  2026-07-25 + voice residual #1 2026-07-28): server-side parity + defence-in-
 *  depth for a negative-intent close phrase the FE's own local isTurnClose()
 *  vocab check (wakeMachine.ts, run inside classifyFinal BEFORE this call is
 *  ever made) did not catch on this turn -- e.g. handsFreeTurn was false, or a
 *  CLARIFY sub-state was open and suppressed the FE check. Distinct from
 *  AssistProposalResponse (no proposal field) and from
 *  AssistDomainChoiceResponse (no needs_domain_choice field) -- MUST be checked
 *  first so a normal proposal read (res.proposal.text) never runs against
 *  this shape (register finding 2026-07-28 12:35 UTC: a close phrase was acked but
 *  never ran the FE close+cooldown because nothing recognised this shape). */
export interface AssistTurnClosedResponse {
  turn_closed: true;
  /** the short fixed ack text (AiRoutes::TURN_CLOSE_ACK, "Okay."). */
  response: string;
  provenance: { provider: string; model: string };
}

export type AssistResponse = AssistProposalResponse | AssistDomainChoiceResponse;

// AssistTurnClosedResponse is intentionally NOT folded into the AssistResponse
// union: AssistResponse also backs the text-chat CopilotRail surface, which
// does not (yet) have a turn-close backstop consumer -- widening the union
// forces every AssistResponse narrowing site (including CopilotRail.tsx) to
// handle a shape it has no code path for, well outside voice residual #1
// locked scope. isBackendTurnClosed() below narrows via an unknown-typed
// guard instead (standard user-defined type-guard pattern), so think()
// alone gains the new branch with zero blast radius elsewhere.

/** Type guard: is this the "which domain?" clarification rather than an answer? */
export function isDomainChoice(r: AssistResponse): r is AssistDomainChoiceResponse {
  return (r as AssistDomainChoiceResponse).needs_domain_choice === true;
}

/** Type guard: is this the BE turn-close backstop (voice residual #1)? Takes
 *  `unknown` (rather than AssistResponse) deliberately -- see the note above
 *  AssistResponse for why AssistTurnClosedResponse is not part of that union;
 *  TypeScript still narrows the call-site argument correctly on this predicate
 *  regardless of the parameter's own declared type. */
export function isBackendTurnClosed(r: unknown): r is AssistTurnClosedResponse {
  return typeof r === 'object' && r !== null && (r as AssistTurnClosedResponse).turn_closed === true;
}

export interface ActiveDomainState {
  active: DomainScope | null;
  domains: DomainOption[];
}

/** localStorage key for instant active-domain restore (UI only, per browser). */
export const ACTIVE_DOMAIN_LS_KEY = 'bc_active_domain_project_id';

export function readLocalActiveDomain(): number | null {
  try {
    const v = window.localStorage.getItem(ACTIVE_DOMAIN_LS_KEY);
    const n = v ? Number.parseInt(v, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function writeLocalActiveDomain(projectId: number | null): void {
  try {
    if (projectId && projectId > 0) {
      window.localStorage.setItem(ACTIVE_DOMAIN_LS_KEY, String(projectId));
    } else {
      window.localStorage.removeItem(ACTIVE_DOMAIN_LS_KEY);
    }
  } catch {
    /* storage unavailable — non-fatal, server remains the source of truth */
  }
}

/**
 * Ask the copilot for an advisory proposal (or a domain-choice clarification).
 * Mock mode returns an honest canned advisory so the dev-mode rail keeps working
 * with zero backend; real mode calls the AI service.
 */
export async function assist(prompt: string, ctx: AssistContext = {}): Promise<AssistResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 900)); // calibration beat
    const text = `You could start by breaking “${prompt.slice(0, 80)}” into one concrete next step and reviewing the current ${ctx.engine ?? 'workspace'} activity before acting.`;
    return {
      advisory: true,
      proposal: { text, rationale: 'Mock mode — no model was called; this is canned demo data.', confidence: 'low' },
      provenance: { provider: 'Aura', model: 'Copilot' },
      display_text: text,
      narration_segments: [{ text, pauseAfterMs: 0 }],
      domain_scope: null,
      sections: [{ heading: '', paragraphs: [text] }],
    };
  }

  return api.post<AssistResponse>('/ai/assist', {
    prompt,
    engine: ctx.engine ?? null,
    subject_type: ctx.subject_type ?? null,
    subject_id: ctx.subject_id ?? null,
    context: ctx.context ?? {},
    ...(ctx.projectId ? { project_id: ctx.projectId } : {}),
  });
}

/** The user's active domain + all connected domains for the active workspace. */
export async function getActiveDomain(): Promise<ActiveDomainState> {
  if (USE_MOCK_DATA) {
    return { active: null, domains: [] };
  }
  return api.get<ActiveDomainState>('/ai/active-domain');
}

/** Persist the user's active domain (server validates it belongs to the workspace). */
export async function setActiveDomain(projectId: number): Promise<{ ok: boolean; active: DomainScope | null }> {
  if (USE_MOCK_DATA) {
    return { ok: true, active: null };
  }
  return api.post<{ ok: boolean; active: DomainScope | null }>('/ai/active-domain', { project_id: projectId });
}

/* ============================================================================
 * Content-action recommender (P3 Lane 3D — FE client for the merged 3A verb).
 *
 * ONE route, POST /ai/recommend, two modes (mirrors ai-service/Http/AiRoutes):
 *   propose (default): { prompt?, context?, project_id? } -> a domain-scoped,
 *     evidence-grounded content-action proposal, OR needs_domain_choice, OR an
 *     honest no-signal / no-domains next step (can_prepare:false).
 *   accept: { accept:true, channel, topic, project_id } -> the pinned §2.7
 *     content-action BRIEF payload + `next.questions` (ONLY the missing brief
 *     fields) + connectors:{status:'gated'} (never publish/schedule/send).
 *
 * CONNECTORS STAY GATED (invariant 5): this client only proposes + prepares a
 * DRAFT brief. It performs NO publish, NO schedule, NO send. The final handoff
 * submits the completed §2.7 brief to the existing draft intake (Lanes 3B/3C),
 * which creates the DRAFT behind the unchanged human approval gate and returns
 * its canonical edit URL. When that intake is not yet wired on an environment,
 * the handoff fails CLOSED to an honest "draft workspace connecting" posture —
 * it NEVER fabricates a draft (invariant 4: no invented data).
 * -------------------------------------------------------------------------- */

/** The channels a content-action brief may target (pinned §2.7, mirrors 3A). */
export type BriefChannel = 'social' | 'blog' | 'newsletter';

/** A pinned §2.7 evidence reference — a pointer to a STORED, source-labelled row. */
export interface EvidenceRef {
  kind: string;
  project_id: number;
  hostname: string;
  summary: string;
  source_label: string;
}

/** The server-chosen content action in a propose response. */
export interface ProposedAction {
  channel: BriefChannel;
  topic: string;
  domain: DomainScope;
  evidence: EvidenceRef[];
}

/** propose result: an evidence-grounded proposal OR an honest no-signal reply. */
export interface RecommendProposalResponse {
  advisory: true;
  recommend: true;
  needs_domain_choice?: false;
  /** true only when a concrete, evidence-backed action is proposed. */
  can_prepare: boolean;
  reply: string;
  proposed_action: ProposedAction | null;
  domain_scope?: DomainScope | null;
  display_text?: string;
  narration_segments?: AssistNarrationSegment[];
  provenance: { provider: string; model: string };
}

/** propose result: "which domain should I suggest a content action for?". */
export interface RecommendDomainChoiceResponse {
  advisory: true;
  recommend: true;
  needs_domain_choice: true;
  can_prepare: false;
  reply: string;
  domains: DomainOption[];
  display_text?: string;
  narration_segments?: AssistNarrationSegment[];
  provenance: { provider: string; model: string };
}

export type RecommendProposeResponse =
  | RecommendProposalResponse
  | RecommendDomainChoiceResponse;

/** The eight §2.7 brief fields (channel + topic pre-filled by the recommender). */
export interface ContentActionBrief {
  channel: BriefChannel | '';
  objective: string;
  audience: string;
  topic: string;
  tone: string;
  format: string;
  cta: string;
  timing_hint: string;
}

/** The pinned §2.7 content-action payload the intake consumes. */
export interface ContentAction {
  source: 'aura_action';
  workspace_scoped: true;
  brief: ContentActionBrief;
  evidence: EvidenceRef[];
  domain: { project_id: number; hostname: string; display_name: string };
  missing_brief_fields: string[];
}

/** One missing-field question to ask at intake (field + human prompt). */
export interface BriefQuestion {
  field: string;
  question: string;
}

/** Connector gate state — must be fully closed for every content action. */
export interface ConnectorGate {
  status: 'gated';
  live: false;
  publish: false;
  schedule: false;
  send: false;
  note: string;
}

/** accept result (DRAFT): the brief payload + the guided-brief questions + gated connectors. */
export interface RecommendAcceptDraftResponse {
  advisory: true;
  recommend: true;
  accepted: true;
  unavailable?: false;
  content_action: ContentAction;
  next: {
    flow: 'draft';
    intake_module: 'content-studio' | 'marketing';
    questions: BriefQuestion[];
    status_note: string;
  };
  connectors: ConnectorGate;
  provenance: { provider: string; model: string };
}

/**
 * accept result (UNAVAILABLE): blog/article intent FAILS HONESTLY. The live
 * backend creates NO record anywhere, preserves the SEO recommendation, and
 * returns HTTP 422 `blog_draft_unavailable` (never a draft-ready). The shared
 * api client throws an ApiError on the 422 and drops the top-level body (only
 * status + code + message survive on ApiError; the echoed `content_action` is
 * NOT reachable through the client), so `recommendAccept` maps that ApiError to
 * THIS typed result. The preserved topic + SEO evidence + domain rendered on the
 * honest card come from the ProposedAction the panel already holds — the server
 * re-derives an identical evidence set from the same domain + topic — so nothing
 * is re-fetched (no duplicate audit event) and no out-of-scope file is touched.
 * This is NOT an error to retry: it is an on-the-roadmap posture. Frontend-only.
 */
export interface RecommendAcceptUnavailableResponse {
  advisory: true;
  recommend: true;
  accepted: false;
  unavailable: true;
  /** echoed from the accept request so the card can label the preserved intent. */
  channel: BriefChannel;
  topic: string;
  error: { code: 'blog_draft_unavailable'; message: string };
  next: { flow: 'unavailable' };
}

/** One SEO-evidence row cited on a populated long-form blog draft (permissive:
 *  the server may send a lighter shape than EvidenceRef). */
export interface BlogDraftEvidence {
  summary: string;
  source_label: string;
  url?: string;
}

/** AI provenance recorded on a generated draft (REAL model id — never brand-
 *  masked here; the brand mask lives only on the top-level `provenance`). */
export interface BlogDraftProvenance {
  model: string;
  prompt_version: string;
}

/**
 * A POPULATED long-form blog_article draft — the P1 happy path. Returned on the
 * accept 200 alongside `next.flow==='blog_draft'`. `body_md` is the safe render
 * source; `body_html` is used ONLY for the Copy-HTML clipboard action.
 */
/**
 * The EXACT SEO target landing page carried on an SEO-generated blog draft
 * (WS1). primary_keyword links to target_page in the first + final paragraph;
 * the value is server-derived from the ranked-organic dataset (never guessed).
 */
export interface BlogDraftSeoTarget {
  primary_keyword: string;
  secondary_keywords?: string[];
  target_page: string;
  position?: number | null;
  recommendation_id?: string;
}

export interface BlogDraft {
  id: number;
  workspace_id: number;
  content_type: 'blog_article';
  title: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  body_md: string;
  body_html: string;
  cta: string;
  /** Yoast focus keyword (the primary SEO keyword). Optional (legacy rows). */
  focus_keyphrase?: string;
  /** WS1: the EXACT SEO target landing page (null for non-SEO drafts). */
  seo_target?: BlogDraftSeoTarget | null;
  seo_evidence: BlogDraftEvidence[];
  ai_provenance: BlogDraftProvenance;
  status: 'ready';
}

/**
 * accept result (BLOG DRAFT): blog/article intent SUCCEEDS — the backend
 * generated + persisted a `blog_article` row and returns it populated (HTTP
 * 200). This is the P1 replacement for the interim 422; the 422 honest card is
 * RETAINED as the failure/flag-off fallback (RecommendAcceptUnavailableResponse).
 */
export interface RecommendAcceptBlogDraftResponse {
  accepted: true;
  content_type: 'blog_article';
  draft: BlogDraft;
  next: { flow: 'blog_draft' };
  /** optional passthrough envelope fields the server may also send. */
  advisory?: true;
  recommend?: true;
  provenance?: { provider: string; model: string };
}

export type RecommendAcceptResponse =
  | RecommendAcceptDraftResponse
  | RecommendAcceptBlogDraftResponse
  | RecommendAcceptUnavailableResponse;

/** Type guard: did accept SUCCEED with a populated long-form blog_article draft? */
export function isRecommendBlogDraft(
  r: RecommendAcceptResponse,
): r is RecommendAcceptBlogDraftResponse {
  const b = r as RecommendAcceptBlogDraftResponse;
  return b.next?.flow === 'blog_draft' && b.draft != null && b.accepted === true;
}

/** Type guard: did accept FAIL HONESTLY (blog not-available-yet) rather than open a draft? */
export function isRecommendUnavailable(
  r: RecommendAcceptResponse,
): r is RecommendAcceptUnavailableResponse {
  return (r as RecommendAcceptUnavailableResponse).unavailable === true;
}

/** Type guard: is this propose response the "which domain?" clarification? */
export function isRecommendDomainChoice(
  r: RecommendProposeResponse,
): r is RecommendDomainChoiceResponse {
  return (r as RecommendDomainChoiceResponse).needs_domain_choice === true;
}

/** Type guard: does this propose response carry a concrete, preparable action? */
export function recommendHasProposal(
  r: RecommendProposeResponse,
): r is RecommendProposalResponse & { proposed_action: ProposedAction } {
  return (
    !isRecommendDomainChoice(r) &&
    r.can_prepare === true &&
    r.proposed_action != null
  );
}

/**
 * Ask Aura for the single most valuable content action for the active domain.
 * Mock mode returns a canned, honestly-labelled proposal so the dev rail works
 * with zero backend; real mode calls the recommender verb.
 */
export async function recommendPropose(
  prompt: string,
  ctx: AssistContext = {},
): Promise<RecommendProposeResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 700));
    const topic = 'mindful breathing for beginners';
    const reply =
      'For example.com, your Search Console shows growing interest in “' +
      topic +
      '” — you could publish a blog this week. Prepare it?';
    return {
      advisory: true,
      recommend: true,
      can_prepare: true,
      reply,
      proposed_action: {
        channel: 'blog',
        topic,
        domain: { project_id: 1, hostname: 'example.com', display_name: 'Example' },
        evidence: [
          {
            kind: 'gsc_query',
            project_id: 1,
            hostname: 'example.com',
            summary: '“' + topic + '” — 240 Search Console impressions, avg position 11.4',
            source_label: 'Search Console',
          },
        ],
      },
      domain_scope: { project_id: 1, hostname: 'example.com', display_name: 'Example' },
      display_text: reply,
      narration_segments: [{ text: reply, pauseAfterMs: 0 }],
      provenance: { provider: 'Aura', model: 'Copilot' },
    };
  }

  return api.post<RecommendProposeResponse>('/ai/recommend', {
    prompt,
    engine: ctx.engine ?? null,
    context: ctx.context ?? {},
    ...(ctx.projectId ? { project_id: ctx.projectId } : {}),
  });
}

/**
 * Accept a proposal: get the §2.7 brief payload, the missing-field questions to
 * ask one at a time, and the (always gated) connector state. NO draft is created
 * here — the guided brief collects the answers, then submits to the intake.
 */
/**
 * DEV-ONLY toggle: when `VITE_MOCK_BLOG_DRAFT=true`, mock mode returns a POPULATED
 * blog draft so the long-form view can be exercised with zero backend. Default
 * (unset/false) keeps the honest 422 `blog_draft_unavailable` card as the mock
 * blog outcome — so the no-revert guarantee holds in an ordinary mock build.
 */
const MOCK_BLOG_DRAFT_ENABLED =
  import.meta.env.VITE_MOCK_BLOG_DRAFT === 'true';

/** A canned, honestly-labelled populated blog draft for the dev rail. */
function mockBlogDraftResponse(input: {
  projectId: number;
  topic: string;
}): RecommendAcceptBlogDraftResponse {
  const title = input.topic
    ? input.topic.replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Your Blog Article';
  const slug = (input.topic || 'blog-article')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  const body_md = [
    '## ' + title,
    '',
    'This is a **mock** long-form draft generated in dev mode — no model was called.',
    'It shows how the populated blog view renders headings, paragraphs and lists.',
    '',
    '### Why this matters',
    '',
    '- Grounded in your Search Console evidence',
    '- Draft only — nothing is published or sent',
    '- You approve every step',
    '',
    'Aura prepares the draft; you stay in control of publishing.',
  ].join('\n');
  const body_html =
    '<h2>' + title + '</h2>\n<p>This is a <strong>mock</strong> long-form draft ' +
    'generated in dev mode — no model was called.</p>';
  return {
    advisory: true,
    recommend: true,
    accepted: true,
    content_type: 'blog_article',
    draft: {
      id: 0,
      workspace_id: 0,
      content_type: 'blog_article',
      title,
      slug,
      meta_title: title + ' — a practical guide',
      meta_description:
        'A grounded, draft-only guide to ' + (input.topic || 'your topic') +
        '. Prepared by Aura; nothing published or sent.',
      body_md,
      body_html,
      cta: 'Book a session to get started',
      seo_evidence: [
        {
          summary:
            '"' + (input.topic || 'your topic') +
            '" — 240 Search Console impressions, avg position 11.4',
          source_label: 'Search Console',
        },
      ],
      ai_provenance: { model: 'mock', prompt_version: 'bc-content-draft-v1' },
      status: 'ready',
    },
    next: { flow: 'blog_draft' },
    provenance: { provider: 'Aura', model: 'Copilot' },
  };
}

export async function recommendAccept(input: {
  projectId: number;
  channel: BriefChannel;
  topic: string;
}): Promise<RecommendAcceptResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 500));
    // Dev-rail honesty: blog/article intent fails HONESTLY — mirrors the live 422
    // `blog_draft_unavailable`. A blog draft is NEVER fabricated (invariant 4).
    if (input.channel === 'blog' && MOCK_BLOG_DRAFT_ENABLED) {
      return mockBlogDraftResponse(input);
    }
    if (input.channel === 'blog') {
      return {
        advisory: true,
        recommend: true,
        accepted: false,
        unavailable: true,
        channel: input.channel,
        topic: input.topic,
        error: {
          code: 'blog_draft_unavailable',
          message:
            "Blog Draft isn't available yet — it's on the roadmap. I've kept your SEO " +
            'recommendation and the evidence so we can prepare it properly soon. ' +
            'Nothing was created, published or sent.',
        },
        next: { flow: 'unavailable' },
      };
    }
    const brief: ContentActionBrief = {
      channel: input.channel,
      objective: '',
      audience: '',
      topic: input.topic,
      tone: '',
      format: '',
      cta: '',
      timing_hint: '',
    };
    const missing = ['objective', 'audience', 'tone', 'format', 'cta', 'timing_hint'];
    return {
      advisory: true,
      recommend: true,
      accepted: true,
      content_action: {
        source: 'aura_action',
        workspace_scoped: true,
        brief,
        evidence: [],
        domain: { project_id: input.projectId, hostname: 'example.com', display_name: 'Example' },
        missing_brief_fields: missing,
      },
      next: {
        flow: 'draft',
        intake_module: input.channel === 'newsletter' ? 'marketing' : 'content-studio',
        questions: [
          { field: 'objective', question: 'What is the main goal of this piece — awareness, leads, or bookings?' },
          { field: 'audience', question: 'Who is it for?' },
          { field: 'tone', question: 'What tone should it strike — friendly, expert, or playful?' },
          { field: 'format', question: 'What format works best — a how-to, a listicle, an announcement, or a story?' },
          { field: 'cta', question: 'What should readers do next?' },
          { field: 'timing_hint', question: 'When would you like it ready?' },
        ],
        status_note:
          'This will open a DRAFT for your review. Nothing is published or sent — you approve every step.',
      },
      connectors: {
        status: 'gated',
        live: false,
        publish: false,
        schedule: false,
        send: false,
        note: 'Publishing and sending connectors are not enabled. Aura prepares drafts only.',
      },
      provenance: { provider: 'Aura', model: 'Copilot' },
    };
  }

  try {
    return await api.post<RecommendAcceptResponse>('/ai/recommend', {
      accept: true,
      project_id: input.projectId,
      channel: input.channel,
      topic: input.topic,
    });
  } catch (e) {
    // Blog/article intent FAILS HONESTLY server-side (HTTP 422
    // `blog_draft_unavailable`): no record is created, the SEO recommendation is
    // preserved, and this must NOT surface as a generic "try again" error. The
    // shared client drops the non-2xx body, so map the ApiError (status + code +
    // message are sufficient — the preserved evidence is rendered from the
    // ProposedAction the panel already holds) to the typed unavailable result.
    // GATE STRICTLY on the code so every OTHER error rethrows byte-identically.
    if (e instanceof ApiError && e.status === 422 && e.code === 'blog_draft_unavailable') {
      return {
        advisory: true,
        recommend: true,
        accepted: false,
        unavailable: true,
        channel: input.channel,
        topic: input.topic,
        error: { code: 'blog_draft_unavailable', message: e.message },
        next: { flow: 'unavailable' },
      };
    }
    throw e;
  }
}

// Re-export the reconciled intake surface (single source of truth lives in the
// pure, api-free voice/actions/contentActionModel so it stays node --test-able).
export { DRAFT_INTAKE_PATHS };
export type { DraftHandoff };

/**
 * Submit the completed §2.7 content-action brief to the EXISTING draft intake
 * (merged Lanes 3B/3C — there is no `/from-brief` sub-route):
 *   content-studio -> POST /social/content    (body.content_action.source==='aura_action')
 *   marketing      -> POST /marketing/campaigns (top-level body.source==='aura_action')
 * `answers` carries any late/extra field the server still needs (merged over the
 * pre-filled brief server-side; never fabricated here). On success the intake
 * creates a DRAFT behind its unchanged human-approval gate and returns its id +
 * canonical edit URL. Connectors are never touched: this only prepares a draft.
 * When the intake is not wired on an environment (404/501/503 or mock) it fails
 * CLOSED to `pending` — never a fabricated draft.
 */
export async function submitContentActionBrief(
  action: ContentAction,
  intakeModule: 'content-studio' | 'marketing',
  answers: Record<string, string> = {},
): Promise<DraftHandoff> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 600));
    // Dev/mock: no real intake — honest "prepared, not yet wired" posture.
    return { status: 'pending', intake_module: intakeModule };
  }

  const path = DRAFT_INTAKE_PATHS[intakeModule];
  const body = buildIntakeBody(action, intakeModule, answers);
  try {
    const res = await api.post<IntakeResponseShape>(path, body);
    return classifyIntakeResponse(res, intakeModule);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501 || e.status === 503)) {
      // Intake not wired on this environment -> fail closed, no fabricated draft.
      return { status: 'pending', intake_module: intakeModule };
    }
    throw e;
  }
}
